import type {
  MetaCapiEventFilters,
  MetaCapiEventName,
  MetaCapiEventSummary,
  MetaCapiStatus,
} from "../types";

/**
 * Almikia Epic EM (TASK-069): pure helpers of the "Eventos Meta" screen (direccion only). The
 * stored payloads are already free of personal data; the display helpers below still mask any
 * long digit run and shorten hashes, so a stray phone number can never be rendered.
 */

export const META_EVENTS_PAGE_SIZE = 25;
export const META_EVENT_STATUSES: MetaCapiStatus[] = [
  "pending",
  "sent",
  "failed",
  "abandoned",
];
export const META_EVENT_NAMES: MetaCapiEventName[] = [
  "lead_calificado",
  "cita",
  "compra",
];

const HASH_PATTERN = /\b[a-f0-9]{32,}\b/gi;
const LONG_DIGITS_PATTERN = /\d{8,}/g;
const HASH_KEEP = 8;
const MASKED_DIGITS = "[oculto]";

export type MetaEventsFilterState = {
  status: MetaCapiStatus | "";
  eventName: MetaCapiEventName | "";
  from: string;
  to: string;
  page: number;
};

export const EMPTY_META_EVENTS_FILTERS: MetaEventsFilterState = {
  status: "",
  eventName: "",
  from: "",
  to: "",
  page: 1,
};

/** Builds the provider filters from the screen state; empty values are omitted. */
export const buildMetaEventFilters = (
  state: MetaEventsFilterState,
): MetaCapiEventFilters => ({
  ...(state.status ? { status: state.status } : {}),
  ...(state.eventName ? { eventName: state.eventName } : {}),
  ...(state.from ? { from: state.from } : {}),
  ...(state.to ? { to: state.to } : {}),
  page: Math.max(state.page, 1),
  perPage: META_EVENTS_PAGE_SIZE,
});

/** A range is usable when it is open on a side or `from` is not after `to`. */
export const isValidEventsRange = (from: string, to: string): boolean =>
  !from || !to || from <= to;

/** Only failed or abandoned events can be retried. */
export const isRetryable = (event: Pick<MetaCapiEventSummary, "status">) =>
  event.status === "failed" || event.status === "abandoned";

/** True when any listed event's last attempt reported missing configuration. */
export const hasMissingConfig = (events: MetaCapiEventSummary[]): boolean =>
  events.some((event) => event.last_log_status === "sin_configuracion");

/** Events sent by a "process pending" run, from the "sent" totals before and after. */
export const processedCount = (sentBefore: number, sentAfter: number): number =>
  Math.max(sentAfter - sentBefore, 0);

/** Hashes keep their first characters; digit runs of 8 or more are masked. */
export const maskSensitiveText = (text: string): string =>
  text
    .replace(HASH_PATTERN, (hash) => `${hash.slice(0, HASH_KEEP)}…`)
    .replace(LONG_DIGITS_PATTERN, MASKED_DIGITS);

/** Pretty, masked JSON of a stored payload; an empty string when there is none. */
export const formatPayload = (payload: Record<string, unknown> | null) =>
  payload ? maskSensitiveText(JSON.stringify(payload, null, 2)) : "";

/** i18n keys. */
export const eventNameKey = (name: MetaCapiEventName) =>
  `crm.metaOrigin.events.name.${name}`;
export const eventStatusKey = (status: MetaCapiStatus) =>
  `crm.metaOrigin.events.status.${status}`;
